import type {AttachmentView, AttachmentStatus} from '@api/attachment/api.d';

export interface AttachmentStatuses {
    isCompleted: boolean;
    isPending: boolean;
    isRequired: boolean;
}

export interface AttachmentWithStatuses extends AttachmentView {
    isCompleted: boolean;
    isPending: boolean;
}

const COMPLETED_STATUSES: AttachmentStatus[] = ['accepted', 'in_progress'];

/**
 * Checks if attachment status is considered completed.
 * Completed means: accepted or in_progress.
 */
export function getIsAttachmentCompleted(status: AttachmentStatus): boolean {
    return COMPLETED_STATUSES.includes(status);
}

/**
 * Calculates attachment statuses based on current state.
 */
export function getAttachmentStatuses(attachment: AttachmentView): AttachmentStatuses {
    const isCompleted = getIsAttachmentCompleted(attachment.status);

    return {
        isCompleted,
        isPending: !isCompleted,
        isRequired: attachment.is_required
    };
}

/**
 * Enriches attachment with computed statuses.
 */
export function getAttachmentWithStatuses(attachment: AttachmentView): AttachmentWithStatuses {
    const statuses = getAttachmentStatuses(attachment);

    return {
        ...attachment,
        isCompleted: statuses.isCompleted,
        isPending: statuses.isPending
    };
}

/**
 * Checks if there are any pending required attachments.
 */
export function getHasPendingRequiredAttachments(attachments: AttachmentView[]): boolean {
    if (!attachments || attachments.length === 0) {
        return false;
    }

    return attachments.some(
        (attachment) => attachment.is_required && !getIsAttachmentCompleted(attachment.status)
    );
}

/**
 * Gets the first pending required attachment.
 * Returns null if no pending required attachments found.
 */
export function getFirstPendingRequiredAttachment(
    attachments: AttachmentView[]
): AttachmentView | null {
    if (!attachments || attachments.length === 0) {
        return null;
    }

    return attachments.find(
        (attachment) => attachment.is_required && !getIsAttachmentCompleted(attachment.status)
    ) ?? null;
}

/**
 * Gets the first pending attachment (required or optional).
 * Returns null if no pending attachments found.
 */
export function getFirstPendingAttachment(attachments: AttachmentView[]): AttachmentView | null {
    if (!attachments || attachments.length === 0) {
        return null;
    }

    return attachments.find(
        (attachment) => !getIsAttachmentCompleted(attachment.status)
    ) ?? null;
}

/**
 * Checks if all required attachments are completed.
 */
export function getAreAllRequiredAttachmentsCompleted(attachments: AttachmentView[]): boolean {
    if (!attachments || attachments.length === 0) {
        return true;
    }

    const requiredAttachments = attachments.filter((attachment) => attachment.is_required);

    if (requiredAttachments.length === 0) {
        return true;
    }

    return requiredAttachments.every(
        (attachment) => getIsAttachmentCompleted(attachment.status)
    );
}
